export type ToolCategoryKey =
  | "layout"
  | "convert"
  | "edit"
  | "combine"
  | "split"
  | "security"
  | "export"
  | "cleanup"
  | "formatting"
  | "file-size"
  | "hub"
  | "ai";

export interface ToolDefinition {
  id: string;
  name: string;
  description: string;
  href: string;
  /**
   * Short, human-readable label shown as the small category text on cards.
   * Example: "Layout & cleanup", "Export & sharing".
   */
  categoryLabel: string;
  /**
   * High-level category key used for filters on the tools page.
   */
  categoryKey: ToolCategoryKey;
  /**
   * Tiny badge label (1–3 chars) shown as a visual identifier on cards.
   * Example: "MG" for merge, "SP" for split.
   */
  badge?: string;
}

export const TOOL_DEFINITIONS: ToolDefinition[] = [
  // {
  //   id: "edit-layout-hub",
  //   name: "Edit layout PDF (hub)",
  //   description: "Jump into page size, margins, and contrast layout edits.",
  //   href: "/tools/edit-layout",
  //   categoryLabel: "Layout & cleanup",
  //   categoryKey: "hub",
  //   badge: "HLB",
  // },
  {
    id: "edit-pdf-visual",
    name: "Edit PDF (visual)",
    description: "Open the visual editor to add text, shapes, redactions, and headers/footers.",
    href: "/tools/edit-pdf",
    categoryLabel: "Layout & markup",
    categoryKey: "layout",
    badge: "EDT",
  },
  {
    id: "edit-pdf-layout",
    name: "Edit PDF Layout",
    description: "All-in-one layout editor: crop, rotate, grayscale, page size, and page numbers.",
    href: "/tools/edit-pdf-layout",
    categoryLabel: "Layout & cleanup",
    categoryKey: "layout",
    badge: "LAY",
  },
  {
    id: "merge-pdf",
    name: "Merge PDF",
    description: "Combine multiple PDFs into one document without uploads.",
    href: "/tools/merge",
    categoryLabel: "Combine PDFs",
    categoryKey: "combine",
    badge: "MG",
  },
  {
    id: "rearrange-pdf",
    name: "Rearrange PDF",
    description: "Reorder, rotate, and delete pages within a PDF.",
    href: "/tools/rearrange",
    categoryLabel: "Layout & cleanup",
    categoryKey: "layout",
    badge: "ARR",
  },
  {
    id: "esign-pdf",
    name: "eSign PDF",
    description: "Sign your PDF documents with your electronic signature.",
    href: "/tools/esign-pdf",
    categoryLabel: "Security & Sign",
    categoryKey: "security",
    badge: "SGN",
  },
  {
    id: "image-to-pdf",
    name: "Image to PDF",
    description: "Convert images (JPG, PNG) to a single PDF file.",
    href: "/tools/convert/image-to-pdf",
    categoryLabel: "Combine & Convert",
    categoryKey: "combine",
    badge: "IMG",
  },
  {
    id: "split-pdf",
    name: "Split PDF",
    description: "Extract page ranges into new PDFs directly in your browser.",
    href: "/tools/split",
    categoryLabel: "Split & extract",
    categoryKey: "split",
    badge: "SP",
  },
  {
    id: "watermark-pdf",
    name: "Watermark PDF",
    description: "Apply text watermarks locally on selected pages.",
    href: "/tools/watermark",
    categoryLabel: "Security & compliance",
    categoryKey: "security",
    badge: "WM",
  },
  {
    id: "encrypt-pdf",
    name: "Encrypt PDF",
    description: "Add a password and permissions locally in your browser.",
    href: "/tools/encrypt",
    categoryLabel: "Security & compliance",
    categoryKey: "security",
    badge: "ENC",
  },
  {
    id: "decrypt-pdf",
    name: "Decrypt PDF",
    description: "Remove a PDF password locally in your browser.",
    href: "/tools/decrypt",
    categoryLabel: "Security & compliance",
    categoryKey: "security",
    badge: "DEC",
  },
  {
    id: "pdf-to-image",
    name: "PDF → Image",
    description: "Render PDF pages as PNG or JPG images on-device.",
    href: "/tools/convert/pdf-to-image",
    categoryLabel: "Export & sharing",
    categoryKey: "export",
    badge: "IMG",
  },
  {
    id: "pdf-to-word",
    name: "PDF → Word",
    description: "Extract text into a DOCX file – no OCR, no cloud.",
    href: "/tools/convert/pdf-to-word",
    categoryLabel: "Export & editing",
    categoryKey: "export",
    badge: "DOC",
  },
  {
    id: "pdf-to-html",
    name: "PDF → HTML",
    description: "Convert PDF to clean HTML – no cloud, no upload.",
    href: "/tools/convert/pdf-to-html",
    categoryLabel: "Export & editing",
    categoryKey: "export",
    badge: "HTM",
  },
  {
    id: "crop-pdf",
    name: "Crop PDF",
    description: "Visually crop page boundaries on one or all pages in your browser.",
    href: "/tools/crop",
    categoryLabel: "Layout & cleanup",
    categoryKey: "layout",
    badge: "CR",
  },
  {
    id: "remove-pages",
    name: "Remove Pages",
    description: "Delete pages by entering ranges and previewing the result.",
    href: "/tools/remove-pages",
    categoryLabel: "Cleanup",
    categoryKey: "cleanup",
    badge: "RP",
  },
  {
    id: "add-page-numbers",
    name: "Add Page Numbers",
    description: "Overlay simple page numbers with your preferred style and position.",
    href: "/tools/page-numbers",
    categoryLabel: "Formatting",
    categoryKey: "formatting",
    badge: "#",
  },
  {
    id: "compress",
    name: "Compress PDF Size",
    description: "Open the dedicated landing for compressing PDF size offline in your browser.",
    href: "/tools/compress",
    categoryLabel: "File size",
    categoryKey: "file-size",
    badge: "CP",
  },
  {
    id: "epub-to-pdf",
    name: "EPUB to PDF",
    description: "Convert EPUB books to PDF document.",
    href: "/tools/epub-to-pdf",
    categoryLabel: "Convert",
    categoryKey: "convert",
    badge: "EPB",
  },
  {
    id: "pdf-to-epub",
    name: "PDF to EPUB",
    description: "Convert PDFs into reflowable EPUB.",
    href: "/tools/pdf-to-epub",
    categoryLabel: "Convert",
    categoryKey: "convert",
    badge: "EPB",
  },
  {
    id: "word-to-pdf",
    name: "Word to PDF",
    description: "Convert DOCX documents to PDF locally using your browser.",
    href: "/tools/word-to-pdf",
    categoryLabel: "Convert",
    categoryKey: "combine",
    badge: "DOC",
  },
  {
    id: "markdown-to-pdf",
    name: "Markdown to PDF",
    description: "Convert Markdown files to PDF locally using your browser.",
    href: "/tools/markdown-to-pdf",
    categoryLabel: "Convert",
    categoryKey: "combine",
    badge: "MD",
  },
  {
    id: "invoice-generator",
    name: "Invoice Generator",
    description: "Create professional PDF invoices instantly. Add your logo, signature, calculate taxes and totals.",
    href: "/tools/invoice-generator",
    categoryLabel: "Export",
    categoryKey: "export",
    badge: "INV",
  },
  {
    id: "qrcode-generator",
    name: "QR & Barcode to PDF",
    description: "Generate batch QR codes and barcodes for printing labels and business cards.",
    href: "/tools/qrcode-generator",
    categoryLabel: "Export",
    categoryKey: "export",
    badge: "QR",
  },
  {
    id: "csv-to-pdf",
    name: "CSV / Smart Table to PDF",
    description: "Convert raw CSV data into styled, paginated PDF reports instantly in your browser.",
    href: "/tools/csv-to-pdf",
    categoryLabel: "Export",
    categoryKey: "export",
    badge: "CSV",
  },
  {
    id: "extract-images",
    name: "Extract Images",
    description: "Extract embedded images from PDFs and download as a ZIP file.",
    href: "/tools/extract-images",
    categoryLabel: "Export",
    categoryKey: "cleanup",
    badge: "IMG",
  },
  {
    id: "split-by-size",
    name: "Split by Size",
    description: "Split large PDFs into smaller chunks based on a maximum file size.",
    href: "/tools/split-by-size",
    categoryLabel: "Split",
    categoryKey: "split",
    badge: "SIZE",
  },
  {
    id: "extract-form-data",
    name: "Extract Form Data",
    description: "Extract form field data from PDF forms as JSON.",
    href: "/tools/extract-form-data",
    categoryLabel: "Export",
    categoryKey: "export",
    badge: "📋",
  },
  {
    id: "enhance-pdf",
    name: "Enhance PDF",
    description: "Sharpen text and clean scanned backgrounds.",
    href: "/tools/enhance-pdf",
    categoryLabel: "Edit",
    categoryKey: "edit",
  },
  {
    id: "flatten-pdf",
    name: "Flatten PDF",
    description: "Merge layers to make PDF un-editable.",
    href: "/tools/flatten-pdf",
    categoryLabel: "Edit",
    categoryKey: "edit",
    badge: "Pro",
  },
  {
    id: "ocr-pdf",
    name: "OCR PDF",
    description: "Extract text from scanned PDFs using OCR (tesseract.js).",
    href: "/tools/ocr-pdf",
    categoryLabel: "Export",
    categoryKey: "export",
    badge: "🔍",
  },
  {
    id: "extract-text-ocr",
    name: "Extract Text (OCR)",
    description: "Extract text from images and PDFs using OCR — handles 100+ page PDFs in a web worker.",
    href: "/tools/extract-text-ocr",
    categoryLabel: "Export",
    categoryKey: "export",
    badge: "📝",
  },
  {
    id: "create-pdf",
    name: "Create PDF",
    description: "Create PDFs from scratch with rich text styling — fonts, colors, bold, italic, headings, and alignments.",
    href: "/tools/create-pdf",
    categoryLabel: "Convert",
    categoryKey: "convert",
    badge: "✍️",
  },
  {
    id: "pdf-to-zip",
    name: "PDF to ZIP",
    description: "Export each PDF page as an image and download as a ZIP file.",
    href: "/tools/pdf-to-zip",
    categoryLabel: "Export",
    categoryKey: "export",
    badge: "📦",
  },
  {
    id: "redact-text",
    name: "Redact Text",
    description: "Find and redact sensitive text in PDF documents.",
    href: "/tools/redact-text",
    categoryLabel: "Security",
    categoryKey: "security",
    badge: "🛡️",
  },
  {
    id: "mail-merge",
    name: "Mail Merge PDF",
    description: "Fill PDF templates with CSV data to create bulk personalized documents.",
    href: "/tools/mail-merge",
    categoryLabel: "Combine",
    categoryKey: "combine",
    badge: "👥",
  },
  {
    id: "batch-stamp",
    name: "Batch Stamp / Watermark",
    description: "Upload multiple PDFs, position a stamp visually, and apply to all files.",
    href: "/batch-tools/stamp",
    categoryLabel: "Automation",
    categoryKey: "combine",
    badge: "BAT",
  },
  {
    id: "header-footer",
    name: "Header & Footer",
    description: "Add custom headers and footers with page numbers, dates, and text to your PDFs.",
    href: "/tools/header-footer",
    categoryLabel: "Formatting",
    categoryKey: "formatting",
    badge: "H/F",
  },
  // AI Tools
  {
    id: "chat-with-pdf",
    name: "Chat with PDF",
    description: "Ask questions and chat with your PDF documents using AI.",
    href: "/tools/ai/chat",
    categoryLabel: "AI Tools",
    categoryKey: "ai",
    badge: "💬",
  },
  {
    id: "pdf-quiz",
    name: "Quiz Generator",
    description: "Generate interactive quizzes from PDF content.",
    href: "/tools/ai/quiz",
    categoryLabel: "AI Tools",
    categoryKey: "ai",
    badge: "📝",
  },
  {
    id: "pdf-timeline",
    name: "Timeline Generator",
    description: "Extract chronological events into a visual timeline.",
    href: "/tools/ai/timeline",
    categoryLabel: "AI Tools",
    categoryKey: "ai",
    badge: "📅",
  },
  {
    id: "pdf-summarize",
    name: "Summarize PDF",
    description: "Generate concise summaries of your PDF documents.",
    href: "/tools/ai/summarize",
    categoryLabel: "AI Tools",
    categoryKey: "ai",
    badge: "📄",
  },
  {
    id: "pdf-keypoints",
    name: "Key Points Extractor",
    description: "Extract the most important takeaways from your PDF.",
    href: "/tools/ai/keypoints",
    categoryLabel: "AI Tools",
    categoryKey: "ai",
    badge: "🎯",
  },
  {
    id: "pdf-flashcards",
    name: "Flashcard Generator",
    description: "Create study flashcards from your PDF content.",
    href: "/tools/ai/flashcards",
    categoryLabel: "AI Tools",
    categoryKey: "ai",
    badge: "🃏",
  },
  {
    id: "pdf-studyguide",
    name: "Study Guide Generator",
    description: "Generate comprehensive study guides from your PDFs.",
    href: "/tools/ai/studyguide",
    categoryLabel: "AI Tools",
    categoryKey: "ai",
    badge: "📚",
  },
  {
    id: "bates-numbering",
    name: "Bates Numbering",
    description: "Add sequential Bates numbers to PDFs for legal documents.",
    href: "/tools/bates-numbering",
    categoryLabel: "Automation",
    categoryKey: "combine",
    badge: "BAT",
  },
  {
    id: "dynamic-stamp",
    name: "Dynamic Stamp",
    description: "Add page numbers, dates, and filenames as dynamic stamps.",
    href: "/tools/dynamic-stamp",
    categoryLabel: "Automation",
    categoryKey: "combine",
    badge: "DYN",
  },
  // Solid Additions
  {
    id: "pdf-to-pptx",
    name: "PDF to PowerPoint",
    description: "Convert PDF pages into a PowerPoint presentation — one slide per page.",
    href: "/tools/pdf-to-pptx",
    categoryLabel: "Convert",
    categoryKey: "convert",
    badge: "📊",
  },
  {
    id: "excel-to-pdf",
    name: "Excel / CSV to PDF",
    description: "Upload a CSV or XLSX and render it as a formatted PDF table.",
    href: "/tools/excel-to-pdf",
    categoryLabel: "Convert",
    categoryKey: "convert",
    badge: "📈",
  },
  {
    id: "html-to-pdf",
    name: "HTML to PDF",
    description: "Paste or enter HTML and generate a styled PDF with live preview.",
    href: "/tools/html-to-pdf",
    categoryLabel: "Convert",
    categoryKey: "convert",
    badge: "🌐",
  },
  {
    id: "pdf-repair",
    name: "PDF Repair",
    description: "Attempt to fix corrupted PDFs by re-serializing with lenient parsing.",
    href: "/tools/pdf-repair",
    categoryLabel: "Cleanup",
    categoryKey: "cleanup",
    badge: "🔧",
  },
  {
    id: "page-extractor",
    name: "Page Extractor",
    description: "Visually select pages with thumbnails and extract them as a new PDF.",
    href: "/tools/page-extractor",
    categoryLabel: "Split",
    categoryKey: "split",
    badge: "📑",
  },
  {
    id: "add-bookmarks",
    name: "Add Bookmarks / TOC",
    description: "Add clickable bookmarks and a table of contents to a PDF.",
    href: "/tools/add-bookmarks",
    categoryLabel: "Edit",
    categoryKey: "edit",
    badge: "🔖",
  },
  {
    id: "pdf-metadata",
    name: "PDF Metadata Editor",
    description: "View and edit title, author, subject, keywords, and dates.",
    href: "/tools/pdf-metadata",
    categoryLabel: "Edit",
    categoryKey: "edit",
    badge: "ℹ️",
  },
  // Security Tools
  {
    id: "pdf-permissions",
    name: "PDF Permission Manager",
    description: "Set granular access controls — allow or deny printing, copying, editing, and more.",
    href: "/tools/pdf-permissions",
    categoryLabel: "Security",
    categoryKey: "security",
    badge: "🛡️",
  },
  {
    id: "pdf-security-audit",
    name: "PDF Security Audit",
    description: "Scan a PDF for encryption, scripts, embedded files, metadata, and risk assessment.",
    href: "/tools/pdf-security-audit",
    categoryLabel: "Security",
    categoryKey: "security",
    badge: "🔍",
  },
  {
    id: "pdf-sanitizer",
    name: "PDF Sanitizer",
    description: "Remove JavaScript, embedded files, auto-actions, and metadata for safe sharing.",
    href: "/tools/pdf-sanitizer",
    categoryLabel: "Security",
    categoryKey: "security",
    badge: "🧹",
  },
  {
    id: "deep-metadata-explorer",
    name: "Deep Metadata Explorer",
    description: "Inspect every hidden metadata field — XMP, modification history, hidden layers, JavaScript. Edit or strip it all.",
    href: "/tools/deep-metadata-explorer",
    categoryLabel: "Security",
    categoryKey: "security",
    badge: "🔬",
  },
];

export const TOOL_FILTERS: { key: ToolCategoryKey | "all"; label: string }[] = [
  { key: "all", label: "All" },
  { key: "ai", label: "AI" },
  { key: "layout", label: "Layout" },
  { key: "combine", label: "Combine" },
  { key: "split", label: "Split" },
  { key: "security", label: "Security" },
  { key: "export", label: "Export" },
  { key: "cleanup", label: "Cleanup" },
  { key: "formatting", label: "Formatting" },
  { key: "file-size", label: "File size" },
  { key: "hub", label: "Hubs" },
];
